import { useEffect, useRef, useState } from 'react';
import { X, Download, Twitter } from 'lucide-react';

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function fmt(n) {
  if (!n || isNaN(n)) return '0';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 });
}

function fmtExact(n) {
  if (!n || isNaN(n)) return '0';
  return Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function drawAirdropCard(canvas, { name, settings, points, costs }) {
  const W = 1080, H = 1080;
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  const PAD = 60;

  const roi = settings?.roiScenarios;

  // ── Background ──
  ctx.fillStyle = '#060d1a';
  ctx.fillRect(0, 0, W, H);

  ctx.strokeStyle = 'rgba(255,255,255,0.025)';
  ctx.lineWidth = 1;
  for (let x = 0; x < W; x += 80) { ctx.beginPath(); ctx.moveTo(x,0); ctx.lineTo(x,H); ctx.stroke(); }
  for (let y = 0; y < H; y += 80) { ctx.beginPath(); ctx.moveTo(0,y); ctx.lineTo(W,y); ctx.stroke(); }

  const g1 = ctx.createRadialGradient(0, 0, 0, 0, 0, 680);
  g1.addColorStop(0, 'rgba(16,185,129,0.22)'); g1.addColorStop(1, 'transparent');
  ctx.fillStyle = g1; ctx.fillRect(0, 0, W, H);

  const g2 = ctx.createRadialGradient(W, H, 0, W, H, 700);
  g2.addColorStop(0, 'rgba(59,130,246,0.18)'); g2.addColorStop(1, 'transparent');
  ctx.fillStyle = g2; ctx.fillRect(0, 0, W, H);

  // ── Layout: calculate from top, fill to bottom ──
  // Fixed heights
  const NAME_TOP     = 60;
  const NAME_H       = 160;   // name baseline area
  const DIVIDER_Y    = NAME_TOP + NAME_H;       // 220
  const STAT_GAP     = 28;
  const STAT_Y       = DIVIDER_Y + STAT_GAP;    // 248
  const STAT_H       = 140;
  const ROI_LABEL_Y  = STAT_Y + STAT_H + 36;    // 424
  const ROI_Y        = ROI_LABEL_Y + 28;         // 452
  const URL_H        = 54;
  const BOTTOM_PAD   = 60;
  // ROI cards fill remaining space
  const ROI_CARD_H   = H - ROI_Y - URL_H - BOTTOM_PAD - 20; // ~494

  // ── Name (left) + Points (right) ──
  const nameSize = name.length > 12 ? 76 : name.length > 8 ? 92 : 112;
  ctx.font = `bold ${nameSize}px sans-serif`;
  ctx.fillStyle = '#ffffff';
  ctx.fillText(name, PAD, NAME_TOP + 110);
  const nameW = ctx.measureText(name).width;

  // Underline
  const ulGrad = ctx.createLinearGradient(PAD, 0, PAD + nameW * 0.85, 0);
  ulGrad.addColorStop(0, '#10b981'); ulGrad.addColorStop(1, 'rgba(16,185,129,0)');
  ctx.fillStyle = ulGrad;
  ctx.fillRect(PAD, NAME_TOP + 124, Math.min(nameW, W - PAD * 2), 4);

  // Points right-aligned
  ctx.font = 'bold 78px sans-serif';
  ctx.textAlign = 'right';
  const ptGrad = ctx.createLinearGradient(W - 400, 0, W - PAD, 0);
  ptGrad.addColorStop(0, '#10b981'); ptGrad.addColorStop(1, '#34d399');
  ctx.fillStyle = ptGrad;
  ctx.fillText(fmtExact(points), W - PAD, NAME_TOP + 100);
  ctx.fillStyle = 'rgba(203,213,225,0.85)';
  ctx.font = '700 17px monospace';
  ctx.fillText('POINTS', W - PAD, NAME_TOP + 128);
  ctx.textAlign = 'left';

  // ── Divider ──
  const divGrad = ctx.createLinearGradient(PAD, 0, W - PAD, 0);
  divGrad.addColorStop(0, 'rgba(16,185,129,0.8)');
  divGrad.addColorStop(0.5, 'rgba(59,130,246,0.35)');
  divGrad.addColorStop(1, 'rgba(59,130,246,0)');
  ctx.strokeStyle = divGrad; ctx.lineWidth = 1.5;
  ctx.beginPath(); ctx.moveTo(PAD, DIVIDER_Y); ctx.lineTo(W - PAD, DIVIDER_Y); ctx.stroke();

  // ── Stats ──
  const statW = (W - PAD * 2 - 24) / 2;

  const drawStat = (x, label, value, color) => {
    ctx.fillStyle = 'rgba(255,255,255,0.05)';
    roundRect(ctx, x, STAT_Y, statW, STAT_H, 18); ctx.fill();
    ctx.strokeStyle = color + '55'; ctx.lineWidth = 1.5;
    roundRect(ctx, x, STAT_Y, statW, STAT_H, 18); ctx.stroke();

    const acG = ctx.createLinearGradient(x, 0, x + statW * 0.55, 0);
    acG.addColorStop(0, color); acG.addColorStop(1, 'transparent');
    ctx.fillStyle = acG;
    roundRect(ctx, x, STAT_Y, statW * 0.55, 3, 2); ctx.fill();

    ctx.fillStyle = 'rgba(203,213,225,0.9)';
    ctx.font = '700 15px monospace';
    ctx.fillText(label, x + 22, STAT_Y + 46);

    ctx.fillStyle = color;
    ctx.font = 'bold 44px sans-serif';
    ctx.fillText(value, x + 22, STAT_Y + 108);
  };

  // PnL: negative = costs spent (red), zero or positive = green
  const pnl = -costs;
  const pnlStr = pnl === 0 ? '$0' : pnl > 0 ? `+$${fmt(pnl)}` : `-$${fmt(costs)}`;
  const pnlColor = pnl >= 0 ? '#34d399' : '#f87171';
  drawStat(PAD, 'PNL', pnlStr, pnlColor);
  drawStat(PAD + statW + 24, 'COST / PT', points > 0 ? `$${(costs / points).toFixed(4)}` : '—', '#fbbf24');

  // ── ROI label ──
  ctx.fillStyle = 'rgba(203,213,225,0.75)';
  ctx.font = '700 14px monospace';
  ctx.fillText('ESTIMATED ROI AT TGE', PAD, ROI_LABEL_Y);

  // ── ROI cards ──
  if (roi) {
    const scenarios = [
      { label: 'BEAR', val: points * roi.bearish, mult: roi.bearish, color: '#f87171', glow: 'rgba(248,113,113,0.10)', border: 'rgba(248,113,113,0.45)' },
      { label: 'BASE', val: points * roi.base,    mult: roi.base,    color: '#60a5fa', glow: 'rgba(96,165,250,0.10)',  border: 'rgba(96,165,250,0.45)' },
      { label: 'BULL', val: points * roi.bullish,  mult: roi.bullish, color: '#34d399', glow: 'rgba(52,211,153,0.10)', border: 'rgba(52,211,153,0.45)' },
    ];

    const cw = (W - PAD * 2 - 32) / 3;
    const ch = Math.round(cw * 1.1); // nearly square — slightly taller than wide

    // Extra vertical space distributed as padding above/below cards
    const totalCardBlock = ch + 28 + 54 + 60; // card + gap-url + url + bottom
    const extraPad = (H - ROI_Y - totalCardBlock) / 2;
    const cardStartY = ROI_Y + extraPad;

    scenarios.forEach((sc, i) => {
      const cx = PAD + i * (cw + 16);
      const cy = cardStartY;

      // Card bg + border
      ctx.fillStyle = sc.glow;
      roundRect(ctx, cx, cy, cw, ch, 22); ctx.fill();
      ctx.strokeStyle = sc.border; ctx.lineWidth = 1.5;
      roundRect(ctx, cx, cy, cw, ch, 22); ctx.stroke();

      // Top accent bar
      const topGrad = ctx.createLinearGradient(cx, 0, cx + cw, 0);
      topGrad.addColorStop(0, sc.color); topGrad.addColorStop(1, sc.glow);
      ctx.fillStyle = topGrad;
      roundRect(ctx, cx, cy, cw, 5, 2); ctx.fill();

      // ── Label pill ──
      const pillW = 130, pillH = 44;
      const pillX = cx + cw / 2 - pillW / 2;
      const pillY = cy + ch * 0.10;
      ctx.fillStyle = sc.color + '22';
      roundRect(ctx, pillX, pillY, pillW, pillH, 22); ctx.fill();
      ctx.strokeStyle = sc.color + '88'; ctx.lineWidth = 1.5;
      roundRect(ctx, pillX, pillY, pillW, pillH, 22); ctx.stroke();
      ctx.fillStyle = sc.color;
      ctx.font = '800 20px monospace';
      ctx.textAlign = 'center';
      ctx.fillText(sc.label, cx + cw / 2, pillY + 29);

      // ── Value — vertically centered ──
      const valStr = `$${fmt(sc.val)}`;
      const valSize = valStr.length > 8 ? 40 : valStr.length > 6 ? 48 : 56;
      ctx.fillStyle = '#ffffff';
      ctx.font = `bold ${valSize}px sans-serif`;
      ctx.fillText(valStr, cx + cw / 2, cy + ch * 0.58);

      // ── Price per point — label + value ──
      ctx.fillStyle = 'rgba(203,213,225,0.55)';
      ctx.font = '600 12px monospace';
      ctx.fillText('PRICE / PT', cx + cw / 2, cy + ch * 0.76);
      ctx.fillStyle = sc.color;
      ctx.font = 'bold 20px monospace';
      ctx.fillText(`$${sc.mult}`, cx + cw / 2, cy + ch * 0.90);

      ctx.textAlign = 'left';
    });

    // ── farmdash.app centered below cards ──
    const urlY = cardStartY + ch + 42;
    const urlGrad = ctx.createLinearGradient(W/2 - 200, 0, W/2 + 200, 0);
    urlGrad.addColorStop(0, '#10b981'); urlGrad.addColorStop(1, '#3b82f6');
    ctx.fillStyle = urlGrad;
    ctx.font = 'bold 32px monospace';
    ctx.textAlign = 'center';
    ctx.fillText('farmdash.app', W / 2, urlY);
    ctx.textAlign = 'left';
  }
}

export const AirdropShareModal = ({ name, settings, points, costs, isDark, onClose }) => {
  const canvasRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      drawAirdropCard(canvas, { name, settings, points, costs });
      setReady(true);
    }, 80);
    return () => clearTimeout(timer);
  }, [name]);

  const download = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `${name.toLowerCase().replace(/\s+/g, '-')}-farmdash.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const shareOnX = () => {
    download();
    setTimeout(() => {
      const text = encodeURIComponent(
        `My ${name} stats on FarmDash 🌾\n\n${Number(points).toLocaleString('en-US')} points accumulated\n\nTrack your airdrops 👇\nfarmdash.app`
      );
      window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank');
    }, 600);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div
        className={`relative w-full max-w-xl rounded-2xl border shadow-2xl p-5 ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'}`}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className={`font-bold text-base ${isDark ? 'text-white' : 'text-gray-900'}`}>Share your {name} stats</h3>
            <p className={`text-xs mt-0.5 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Ready to post on X</p>
          </div>
          <button onClick={onClose} className={`p-1.5 rounded-lg ${isDark ? 'hover:bg-slate-800 text-slate-400' : 'hover:bg-gray-100 text-gray-500'}`}>
            <X size={18} />
          </button>
        </div>

        <div className="relative rounded-xl overflow-hidden bg-slate-950 mb-4" style={{ aspectRatio: '1/1' }}>
          <canvas ref={canvasRef} className="w-full h-full" />
          {!ready && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>

        <div className="flex gap-3">
          <button
            onClick={download}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 text-white font-semibold rounded-xl transition-all text-sm shadow-lg"
          >
            <Download size={15} />
            Download PNG
          </button>
          <button
            onClick={shareOnX}
            className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm transition-all border ${isDark ? 'bg-slate-800 border-slate-600 text-white hover:bg-slate-700' : 'bg-gray-50 border-gray-200 text-gray-800 hover:bg-gray-100'}`}
          >
            <Twitter size={15} />
            Post on X
          </button>
        </div>
        <p className={`text-xs text-center mt-3 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
          "Post on X" descarga la imagen y abre Twitter — adjúntala manualmente al tweet
        </p>
      </div>
    </div>
  );
};